import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Save, ExternalLink, Unplug, RefreshCw, AlertTriangle, CheckCircle2, Calendar, Loader2, CreditCard, Eye, EyeOff, Check } from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

/**
 * Integrations tab — Google Calendar, Stripe payments.
 */
export default function IntegrationsTab({
  gcalStatus,
  gcalTesting, gcalTest, gcalCalendars,
  syncCalendarId, setSyncCalendarId,
  busyCalendarIds, toggleBusyCalendar,
  savingCalSettings, saveCalendarSettings,
  loadCalendars, testGoogleConnection, disconnectGoogle,
  settings, onSettingsChange,
}) {
  const oauthHref = `${API}/oauth/google/start?origin=${encodeURIComponent(window.location.origin)}`;
  const [showSecret, setShowSecret] = useState(false);
  const [stripeTestLoading, setStripeTestLoading] = useState(false);
  const [stripeTestResult, setStripeTestResult] = useState(null); // 'ok' | 'fail'

  const u = onSettingsChange; // alias

  // Compute deposit amount label for preview
  const depositType = settings?.deposit_type || 'flat';
  const depositAmt = parseFloat(settings?.deposit_amount || 20);

  const testStripeConnection = async () => {
    setStripeTestLoading(true);
    setStripeTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/payments/test-key`,
        { secret_key: settings?.stripe_secret_key || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStripeTestResult(res.data.valid ? 'ok' : 'fail');
      toast.success(res.data.valid ? 'Stripe key is valid' : 'Invalid Stripe key');
    } catch {
      setStripeTestResult('fail');
      toast.error('Could not verify Stripe key');
    }
    setStripeTestLoading(false);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 rounded-2xl border shadow-sm">
        <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
          <Calendar size={20} className="text-blue-600" />
        </div>
        <div>
          <h3 className="text-base font-semibold" style={{ fontFamily: 'Playfair Display' }}>Google Calendar</h3>
          <p className="text-xs text-muted-foreground">Sync bookings and block availability from your Google calendars</p>
        </div>
      </div>

      <Separator />

      {!gcalStatus.connected ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge className="bg-gray-100 text-gray-600 rounded-full text-xs">Not connected</Badge>
          </div>
          <p className="text-sm text-muted-foreground">Connect your Google account to automatically sync new bookings and block off busy times in your calendar.</p>
          <a href={oauthHref} target="_blank" rel="noopener noreferrer">
            <Button className="rounded-xl" data-testid="connect-google-btn">
              <ExternalLink size={14} className="mr-2" /> Connect Google Calendar
            </Button>
          </a>
        </div>
      ) : gcalStatus.token_invalid ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-red-100 text-red-700 rounded-full text-xs flex items-center gap-1">
              <AlertTriangle size={10} /> Token Expired / Revoked
            </Badge>
            <span className="text-sm text-muted-foreground">{gcalStatus.email}</span>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 space-y-1">
            <p className="font-semibold">⚠️ Action required: Reconnect your calendar</p>
            <p className="text-xs text-red-600">Your Google Calendar access was revoked or expired. This stops booking sync and availability blocking. Click below to reconnect.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a href={oauthHref} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-xl" data-testid="reconnect-google-btn">
                <RefreshCw size={14} className="mr-2" /> Reconnect Google Calendar
              </Button>
            </a>
            <Button variant="outline" onClick={disconnectGoogle} className="rounded-xl text-sm">
              <Unplug size={14} className="mr-1" /> Disconnect
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Badge className="bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                <CheckCircle2 size={10} /> Connected
              </Badge>
              <span className="text-sm font-medium">{gcalStatus.email}</span>
              {gcalStatus.updated_at && (
                <span className="text-xs text-muted-foreground">
                  Last synced {new Date(gcalStatus.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm" onClick={testGoogleConnection}
                disabled={gcalTesting} className="rounded-xl text-xs h-8"
                data-testid="test-connection-btn"
              >
                {gcalTesting ? <Loader2 size={12} className="mr-1 animate-spin" /> : <RefreshCw size={12} className="mr-1" />}
                {gcalTesting ? 'Testing...' : 'Test Connection'}
              </Button>
              <Button variant="outline" size="sm" onClick={disconnectGoogle} className="rounded-xl text-xs h-8 text-destructive">
                <Unplug size={12} className="mr-1" /> Disconnect
              </Button>
            </div>
          </div>

          {gcalTest && (
            <div className={`rounded-xl p-3 text-sm border ${gcalTest.valid ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {gcalTest.valid
                ? `✅ Connection verified — found ${gcalTest.calendars?.length || 0} calendar${(gcalTest.calendars?.length || 0) !== 1 ? 's' : ''}`
                : <div className="space-y-2">
                    <p>❌ {gcalTest.error}</p>
                    <a href={oauthHref} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="rounded-xl h-7 text-xs mt-1">
                        <RefreshCw size={11} className="mr-1" /> Reconnect Now
                      </Button>
                    </a>
                  </div>
              }
            </div>
          )}

          <Separator />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">Calendar Settings</p>
                <p className="text-xs text-muted-foreground">Choose which calendars to use for syncing and availability</p>
              </div>
              {gcalCalendars.length === 0 && (
                <Button variant="outline" size="sm" onClick={loadCalendars} className="rounded-xl text-xs h-8">
                  <Calendar size={12} className="mr-1" /> Load Calendars
                </Button>
              )}
            </div>

            {gcalCalendars.length === 0 ? (
              <div className="bg-muted/30 rounded-xl p-4 text-sm text-muted-foreground text-center">
                <Calendar size={24} className="mx-auto mb-2 opacity-40" />
                <p>Click "Load Calendars" or "Test Connection" to see your available calendars</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">📅 Create new bookings in</Label>
                  <p className="text-xs text-muted-foreground">New appointments will be added to this calendar</p>
                  <RadioGroup value={syncCalendarId} onValueChange={setSyncCalendarId} className="space-y-1.5">
                    {gcalCalendars.map(cal => (
                      <div key={cal.id} className="flex items-center gap-3 p-2.5 rounded-xl border hover:bg-muted/20 transition-colors">
                        <RadioGroupItem value={cal.id} id={`sync-${cal.id}`} />
                        <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: cal.backgroundColor || '#4285f4' }} />
                        <div className="flex-1 min-w-0">
                          <Label htmlFor={`sync-${cal.id}`} className="text-xs font-medium cursor-pointer">
                            {cal.summary}
                            {cal.primary && <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Primary</span>}
                          </Label>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-foreground">🚫 Block availability from</Label>
                  <p className="text-xs text-muted-foreground">Busy times in these calendars will block booking slots</p>
                  <div className="space-y-1.5">
                    {gcalCalendars.map(cal => (
                      <div key={cal.id} className="flex items-center gap-3 p-2.5 rounded-xl border hover:bg-muted/20 transition-colors">
                        <Checkbox
                          id={`busy-${cal.id}`}
                          checked={busyCalendarIds.includes(cal.id)}
                          onCheckedChange={() => toggleBusyCalendar(cal.id)}
                        />
                        <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: cal.backgroundColor || '#4285f4' }} />
                        <div className="flex-1 min-w-0">
                          <Label htmlFor={`busy-${cal.id}`} className="text-xs font-medium cursor-pointer">
                            {cal.summary}
                            {cal.primary && <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Primary</span>}
                          </Label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Button onClick={saveCalendarSettings} disabled={savingCalSettings} className="rounded-xl w-full" data-testid="save-calendar-settings-btn">
                  {savingCalSettings ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
                  {savingCalSettings ? 'Saving...' : 'Save Calendar Settings'}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>

      {/* ── Stripe Payments ─────────────────────────────────────── */}
      <Card className="p-6 rounded-2xl border shadow-sm" data-testid="stripe-settings-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
            <CreditCard size={20} className="text-violet-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold" style={{ fontFamily: 'Playfair Display' }}>Stripe Payments</h3>
            <p className="text-xs text-muted-foreground">Collect online deposits at the time of booking</p>
          </div>
          {settings?.stripe_secret_key && (
            <Badge className="rounded-full text-xs bg-emerald-100 text-emerald-700">Connected</Badge>
          )}
        </div>

        <Separator className="mb-5" />

        {/* API Keys */}
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-medium flex items-center gap-2">
              Stripe Secret Key
              {settings?.stripe_secret_key_source === 'env' && (
                <Badge className="rounded-full text-[10px] bg-amber-50 text-amber-700 font-normal">from Secrets (env)</Badge>
              )}
              {settings?.stripe_secret_key_source === 'db' && (
                <Badge className="rounded-full text-[10px] bg-emerald-50 text-emerald-700 font-normal">stored in app</Badge>
              )}
            </Label>
            <p className="text-[10px] text-muted-foreground mb-1.5">
              Found in <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">Stripe Dashboard → API Keys</a>. Use <code>sk_live_…</code> for production, <code>sk_test_…</code> for testing. If left blank, we fall back to <code>STRIPE_API_KEY</code> from Secrets.
            </p>
            <div className="relative">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={settings?.stripe_secret_key || ''}
                onChange={e => u('stripe_secret_key', e.target.value)}
                placeholder="sk_live_… or sk_test_…"
                className="rounded-xl pr-10 text-xs font-mono"
                data-testid="stripe-secret-key-input"
              />
              <button
                type="button"
                onClick={() => setShowSecret(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div className="flex gap-2 items-center flex-wrap">
            <Button
              variant="outline" size="sm" className="rounded-xl text-xs"
              onClick={testStripeConnection}
              disabled={stripeTestLoading || !settings?.stripe_secret_key}
              data-testid="stripe-test-btn"
            >
              {stripeTestLoading ? <Loader2 size={12} className="mr-1 animate-spin" /> : null}
              Test connection
            </Button>
            {settings?.stripe_secret_key_source === 'db' && (
              <Button
                variant="ghost" size="sm"
                className="rounded-xl text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={async () => {
                  if (!window.confirm('Remove the saved Stripe key? We\'ll fall back to STRIPE_API_KEY from Secrets if set.')) return;
                  try {
                    const token = localStorage.getItem('token');
                    await axios.post(`${API}/settings/disconnect-stripe`, {}, { headers: { Authorization: `Bearer ${token}` } });
                    toast.success('Stripe key cleared from app');
                    // Re-fetch settings via the parent's reload mechanism
                    const res = await axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } });
                    Object.entries(res.data).forEach(([k, v]) => u(k, v));
                  } catch { toast.error('Failed to clear key'); }
                }}
                data-testid="stripe-disconnect-btn"
              >
                <Unplug size={12} className="mr-1" /> Clear saved key
              </Button>
            )}
            {stripeTestResult === 'ok' && <span className="text-xs text-emerald-600 flex items-center gap-1"><Check size={12} /> Valid</span>}
            {stripeTestResult === 'fail' && <span className="text-xs text-red-500 flex items-center gap-1"><AlertTriangle size={12} /> Invalid key</span>}
          </div>
        </div>

        <Separator className="my-5" />

        {/* Deposit config */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={settings?.stripe_deposit_enabled ?? false}
              onCheckedChange={v => u('stripe_deposit_enabled', v)}
              data-testid="stripe-deposit-toggle"
            />
            <div>
              <Label className="text-xs font-medium">Enable online deposits</Label>
              <p className="text-[10px] text-muted-foreground mt-0.5">Clients pay a deposit when they confirm their booking</p>
            </div>
          </div>

          {settings?.stripe_deposit_enabled && (
            <div className="pl-1 space-y-4">
              {/* Deposit type */}
              <div>
                <Label className="text-xs font-medium">Deposit type</Label>
                <div className="flex gap-3 mt-2">
                  {['flat', 'percent'].map(type => (
                    <button
                      key={type}
                      onClick={() => u('deposit_type', type)}
                      className={`flex-1 py-2 px-3 rounded-xl text-xs border-2 font-medium transition-all ${
                        depositType === type ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:border-primary/30'
                      }`}
                    >
                      {type === 'flat' ? 'Fixed amount (£)' : 'Percentage (% of total)'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount */}
              <div>
                <Label className="text-xs font-medium">{depositType === 'flat' ? 'Deposit amount (£)' : 'Deposit percentage (%)'}</Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">{depositType === 'flat' ? '£' : '%'}</span>
                  <Input
                    type="number" step="1" min="0"
                    value={depositAmt}
                    onChange={e => u('deposit_amount', parseFloat(e.target.value) || 0)}
                    className="rounded-xl w-28 text-sm"
                    data-testid="stripe-deposit-amount"
                  />
                  <span className="text-xs text-muted-foreground">
                    {depositType === 'percent' ? `= £${(depositAmt / 100 * 50).toFixed(0)}–£${(depositAmt / 100 * 100).toFixed(0)} on typical bookings` : 'fixed per booking'}
                  </span>
                </div>
              </div>

              {/* Optional skip */}
              <div className="flex items-center gap-3">
                <Switch
                  checked={settings?.deposit_optional ?? true}
                  onCheckedChange={v => u('deposit_optional', v)}
                  data-testid="stripe-deposit-optional"
                />
                <div>
                  <Label className="text-xs font-medium">Allow "Pay at salon" option</Label>
                  <p className="text-[10px] text-muted-foreground mt-0.5">If off, clients must pay the deposit to complete booking</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground mt-5">
          Click <strong>Save Changes</strong> at the top of the page to apply. Stripe keys are stored securely in your database.
        </p>
      </Card>
    </div>
  );
}
